import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { useImageContext } from "../../../contexts/ImageContext";
import { clearEditorSession } from "../../../state/editorStore";
import "./Activity.css";
import logger from "../../../utils/logger";
import { scaleFitIcon, scaleOriginalIcon } from "../../../icons/assets";
import {
  getCreations,
  mergeDesignItems,
  MY_DESIGNS_FILE_TYPES,
  MY_DESIGNS_PAGE_SIZE,
  fetchCreationImageBlob,
  fetchExportImageBlob,
} from "../../../services/api/editor/background_removal";
import GalleryLoadMore from "../../../components/manage/GalleryLoadMore";
import RoundLoader from "../../../components/ui/RoundLoader";
import FadeInMedia from "../../../components/ui/FadeInMedia";
import { withAuthReadyRequest } from "../../../utils/withAuthReadyRequest";
import { libraryItemDisplayName } from "../../../utils/openLibraryInEditor";

function designNumericId(design) {
  if (design.kind === "export") {
    return design.export_id ?? Number(String(design.id).replace(/^export:/, ""));
  }
  return design.creation_id ?? Number(String(design.id).replace(/^creation:/, ""));
}

function groupDesignsByDate(designsList) {
  const grouped = {};
  designsList.forEach((design) => {
    const date = new Date(design.created_at).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
    });
    if (!grouped[date]) {
      grouped[date] = [];
    }
    grouped[date].push(design);
  });
  return grouped;
}

function appendGroupedDesigns(previousGrouped, newItems) {
  const seen = new Set();
  const flat = [];
  Object.values(previousGrouped)
    .flat()
    .forEach((design) => {
      if (!seen.has(design.id)) {
        seen.add(design.id);
        flat.push(design);
      }
    });
  newItems.forEach((design) => {
    if (!seen.has(design.id)) {
      seen.add(design.id);
      flat.push(design);
    }
  });
  return groupDesignsByDate(flat);
}

const IMAGE_LOAD_CONCURRENCY = 4;
let imageLoadActive = 0;
const imageLoadQueue = [];

function runWithImageLoadLimit(task) {
  return new Promise((resolve, reject) => {
    const run = () => {
      imageLoadActive += 1;
      Promise.resolve()
        .then(task)
        .then(resolve, reject)
        .finally(() => {
          imageLoadActive -= 1;
          const next = imageLoadQueue.shift();
          if (next) next();
        });
    };
    if (imageLoadActive < IMAGE_LOAD_CONCURRENCY) {
      run();
    } else {
      imageLoadQueue.push(run);
    }
  });
}

function ActivityCardImage({ design, getToken, scaleMode }) {
  const cardRef = useRef(null);
  const [src, setSrc] = useState(null);
  const [preferBlob, setPreferBlob] = useState(!design.output_url);
  const [exhausted, setExhausted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    let observer = null;

    setExhausted(false);

    if (design.output_url && !preferBlob) {
      setSrc(design.output_url);
      return undefined;
    }

    const loadImage = () =>
      runWithImageLoadLimit(async () => {
        const token = await getToken();
        if (!token || cancelled) {
          if (!cancelled) setExhausted(true);
          return;
        }

        const blob =
          design.kind === "export"
            ? await fetchExportImageBlob(token, designNumericId(design))
            : await fetchCreationImageBlob(token, designNumericId(design));

        if (cancelled) return;
        if (blob && blob.type && blob.type.includes("json")) {
          throw new Error("Activity image API returned JSON error");
        }
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      }).catch((err) => {
        if (!cancelled) {
          logger.error("Failed to load activity card image", err);
          setExhausted(true);
        }
      });

    const node = cardRef.current;
    if (!node) {
      loadImage();
      return () => {
        cancelled = true;
        if (objectUrl) URL.revokeObjectURL(objectUrl);
      };
    }

    observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer?.disconnect();
          loadImage();
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(node);

    return () => {
      cancelled = true;
      observer?.disconnect();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [design, getToken, preferBlob]);

  return (
    <div ref={cardRef} className="design-card__media">
      <FadeInMedia
        src={src}
        alt={design.file_type || "Design"}
        objectFit={scaleMode}
        loaderSize="sm"
        waiting={!exhausted}
        className={
          design.has_transparency ? "design-card__img--transparent" : ""
        }
        onError={() => {
          if (design.output_url && !preferBlob) {
            setPreferBlob(true);
            setSrc(null);
            return;
          }
          setExhausted(true);
        }}
      />
    </div>
  );
}

function activityLabelForDesign(design) {
  const type = String(design.file_type || design.kind || "").toLowerCase();
  if (type.includes("passport")) return "Passport photo";
  if (type.includes("upscale")) return "Upscale";
  if (type.includes("outpaint") || type.includes("expand")) return "Expand";
  if (type.includes("ai_background") || type.includes("ai")) return "AI background";
  if (design.kind === "export") return "Downloaded export";
  if (type.includes("image") || type.includes("bg")) return "Background removed";
  return "Processed design";
}

function activityMetaLines(design) {
  const name = libraryItemDisplayName(design);
  const format = (
    design.format ||
    design.output_format ||
    design.file_ext ||
    "png"
  ).toUpperCase();
  const when = design.created_at
    ? new Date(design.created_at).toLocaleString()
    : "—";
  return [`File: ${name}`, `Format: ${format}`, `Created: ${when}`];
}

const Activity = () => {
  const navigate = useNavigate();
  const { clearImage, setNormalEntry } = useImageContext();
  const [designs, setDesigns] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadedCount, setLoadedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [sliderValue, setSliderValue] = useState(50);
  const [scaleMode, setScaleMode] = useState("cover");
  const { getToken } = useAuth();

  const imageSize = 200 + sliderValue * 1;

  const fetchActivityPage = useCallback(
    async (skip, { append = false } = {}) => {
      const data = await withAuthReadyRequest(getToken, (token) =>
        getCreations(token, {
          skip,
          limit: MY_DESIGNS_PAGE_SIZE,
          fileTypes: MY_DESIGNS_FILE_TYPES,
          includeExports: true,
        })
      );
      const items = mergeDesignItems(data).filter(
        (item) => item?.file_type !== "library_upload"
      );

      if (append) {
        setDesigns((prev) => appendGroupedDesigns(prev, items));
        setLoadedCount((prev) => prev + items.length);
      } else {
        setDesigns(groupDesignsByDate(items));
        setLoadedCount(items.length);
      }

      setTotalCount(data.total_count ?? items.length);
      setHasMore(Boolean(data.has_more));
      setLoadError(null);
    },
    [getToken]
  );

  const loadActivity = useCallback(async () => {
    try {
      setIsLoading(true);
      setLoadError(null);
      await fetchActivityPage(0, { append: false });
    } catch (error) {
      logger.error("Failed to fetch activity", error);
      setDesigns({});
      setLoadedCount(0);
      setTotalCount(0);
      setHasMore(false);
      setLoadError("Couldn’t load activity. Please refresh and try again.");
    } finally {
      setIsLoading(false);
    }
  }, [fetchActivityPage]);

  const loadMoreActivity = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;
    try {
      setIsLoadingMore(true);
      await fetchActivityPage(loadedCount, { append: true });
    } catch (error) {
      logger.error("Failed to load more activity", error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [fetchActivityPage, hasMore, isLoadingMore, loadedCount]);

  useEffect(() => {
    loadActivity();
  }, [loadActivity]);

  const handleOpenDesign = (design) => {
    clearEditorSession();
    setNormalEntry();
    clearImage();
    if (design.kind === "export") {
      navigate(`/editor?exportId=${designNumericId(design)}`);
      return;
    }
    navigate(`/editor?creationId=${designNumericId(design)}`);
  };

  if (isLoading) {
    return (
      <div className="activity-wrapper">
        <div className="activity-loading">
          <RoundLoader size="lg" label="Loading activity…" />
        </div>
      </div>
    );
  }

  const hasDesigns = Object.keys(designs).length > 0;

  return (
    <div className="activity-wrapper">
      <div className="activity-header">
        <h1>Activity</h1>
        <div className="header-controls" title="Controls">
          <button
            className="control-icon scale-toggle-btn"
            title={scaleMode === "contain" ? "Scale to Fit" : "Scale Original"}
            onClick={() =>
              setScaleMode(scaleMode === "contain" ? "cover" : "contain")
            }
          >
            <img
              src={scaleMode === "contain" ? scaleFitIcon : scaleOriginalIcon}
              alt={scaleMode === "contain" ? "Fit" : "Original"}
              style={{ width: "30px", height: "30px" }}
            />
          </button>
          <div className="slider-container" title="Image Size Controller">
            <input
              type="range"
              min="0"
              max="100"
              value={sliderValue}
              onChange={(e) => setSliderValue(Number(e.target.value))}
              className="volume-slider"
              style={{
                background: `linear-gradient(to right, #0066ff 0%, #0066ff ${sliderValue}%, #333 ${sliderValue}%, #333 100%)`,
              }}
            />
          </div>
        </div>
      </div>

      <div className="activity-main">
        {hasDesigns ? (
          <>
            {Object.entries(designs).map(([date, designsList]) => (
              <div key={date} className="designs-section">
                <div className="designs-date-header">
                  <span className="date-label">{date}</span>
                </div>

                <div
                  className="designs-grid"
                  style={{
                    gridTemplateColumns: `repeat(auto-fill, minmax(${imageSize}px, ${imageSize}px))`,
                  }}
                >
                  {designsList.map((design) => (
                    <div key={design.id} className="activity-card-item">
                      <div
                        className={`design-card${
                          design.has_transparency
                            ? " design-card--transparent"
                            : ""
                        }`}
                        onClick={() => handleOpenDesign(design)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            handleOpenDesign(design);
                          }
                        }}
                      >
                        <ActivityCardImage
                          design={design}
                          getToken={getToken}
                          scaleMode={scaleMode}
                        />
                        <div className="design-overlay">
                          <div className="design-overlay-content">
                            <div className="design-overlay-title">
                              {activityLabelForDesign(design)}
                            </div>
                            {activityMetaLines(design).map((line) => (
                              <div key={line} className="design-overlay-line">
                                {line}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="activity-card-meta">by you</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <GalleryLoadMore
              loadedCount={loadedCount}
              totalCount={totalCount}
              hasMore={hasMore}
              isLoadingMore={isLoadingMore}
              onLoadMore={loadMoreActivity}
            />
          </>
        ) : (
          <div className="no-designs">
            <p>{loadError || "No activity yet. Start creating!"}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Activity;
